import { Static, Type } from "@sinclair/typebox";
import { ApiErrorResponse } from "../../ApiErrorResponse";
import { TypeBoxToType } from "@whatslly/types/TypeBoxToType";

export const AuditLogNamespace = Type.Union([
  Type.Literal("sidecars"),
  Type.Literal("integrations"),
  Type.Literal("users"),
  Type.Literal("userGroups"),
  Type.Literal("settings"),
  Type.Literal("waba"),
  Type.Literal("twilio"),
  Type.Literal("auth"),
  Type.Literal("dlp"),
  Type.Literal("incident"),
  Type.Literal("urlPolicies"),
  Type.Literal("browserControls"),
  Type.Literal("secureStorage"),
  Type.Literal("tuvisContacts"),
  Type.Literal("email"),
  Type.Literal("keywordInsights"),
  Type.Literal("phonesWhitelist"),
  Type.Literal("phonesBlocklist"),
  Type.Literal("blockedPhones"),
  Type.Literal("fraudDetection"),
  Type.Literal("dashboards"),
  Type.Literal("customFilters"),
  Type.Literal("surveillance"),
  Type.Literal("accounts"),
  Type.Literal("archive"),
  Type.Literal("platforms"),
  Type.Literal("saml"),
  Type.Literal("botFlows"),
]);
export type AuditLogNamespace = Static<typeof AuditLogNamespace>;

export const AuditLogMessage = Type.Object({
  entityId: Type.String(),
  orgId: Type.String(),
  namespace: AuditLogNamespace,
  event: Type.String(),
  userId: Type.Optional(Type.String()),
  metadata: Type.Optional(Type.Record(Type.String(), Type.Any())),
});
export type AuditLogMessage = Static<typeof AuditLogMessage> & { userId: string | undefined };

export const AuditLogEntry = Type.Intersect([AuditLogMessage, Type.Object({ _id: Type.String(), createdAt: Type.String() })]);
export type AuditLogEntry = Static<typeof AuditLogEntry>;

export const AuditLogQuery = Type.Object({
  orgId: Type.String(),
  page: Type.Optional(Type.Number()),
  namespace: Type.Optional(Type.Array(AuditLogNamespace)),
  "namespace.exclude": Type.Optional(Type.Array(AuditLogNamespace)),
  entityId: Type.Optional(Type.Array(Type.String())),
  "entityId.exclude": Type.Optional(Type.Array(Type.String())),
  event: Type.Optional(Type.Array(Type.String())),
  "event.exclude": Type.Optional(Type.Array(Type.String())),
  userId: Type.Optional(Type.Array(Type.String())),
  startDate: Type.Optional(Type.String({ format: "date-time" })),
  endDate: Type.Optional(Type.String({ format: "date-time" })),
});
export type AuditLogQuery = Static<typeof AuditLogQuery>;

export const AuditLogCsvQuery = Type.Intersect([
  AuditLogQuery,
  Type.Object({
    startDate: Type.Optional(Type.String({ format: "date-time" })),
    endDate: Type.Optional(Type.String({ format: "date-time" })),
    maskHeaders: Type.Optional(Type.Record(Type.String(), Type.String())),
  }),
]);
export type AuditLogCsvQuery = Static<typeof AuditLogCsvQuery>;

const AuditLogSuccess = Type.Object({
  success: Type.Literal(true),
  logs: Type.Array(AuditLogEntry),
});

export const AuditLogResponse = Type.Union([AuditLogSuccess, ApiErrorResponse]);
export type AuditLogResponse = Static<typeof AuditLogResponse>;

export const AuditLogGetFiltersSuccess = Type.Object({
  success: Type.Literal(true),
  namespaces: Type.Array(Type.String()),
  events: Type.Array(Type.String()),
});
export const AuditLogGetFiltersResponse = Type.Union([AuditLogGetFiltersSuccess, ApiErrorResponse]);

export type AuditLogGetFiltersResponse = TypeBoxToType<typeof AuditLogGetFiltersResponse>;

export function parseAuditLogCSVQueryString<T>(queryString: string): T {
  const result: Record<string, any> = {};

  queryString.split("&").forEach((pair) => {
    if (!pair) return;

    const [rawKey, rawValue = ""] = pair.split("=");

    // Replace `+` with spaces and decode
    const key = decodeURIComponent(rawKey.replace(/\+/g, " "));
    const value = decodeURIComponent(rawValue.replace(/\+/g, " "));

    if (key.includes("[")) {
      // Handle nested keys like "a[b][c]"
      const keys = key.split(/\[|\]/).filter(Boolean);
      let current = result;

      keys.forEach((k, index) => {
        if (index === keys.length - 1) {
          // Last key: Assign value
          if (current[k] === undefined) {
            current[k] = value;
          } else if (Array.isArray(current[k])) {
            current[k].push(value);
          } else {
            current[k] = [current[k], value];
          }
        } else {
          // Create nested objects if they don't exist
          current[k] = current[k] || {};
          current = current[k];
        }
      });
    } else {
      // Handle simple keys
      if (result[key] === undefined) {
        result[key] = value;
      } else if (Array.isArray(result[key])) {
        result[key].push(value);
      } else {
        result[key] = [result[key], value];
      }
    }
  });

  return result as T;
}
